// Parses raw SFSArray into WarState
import { SFSArray } from 'sfs2x-api';

export function parseRawWarData(sfsBattles: SFSArray): WarState {
    const battles: Battle[] = [];

    for (let i = 0; i < sfsBattles.size(); i++) {
        const sfsBattle = sfsBattles.getSFSObject(i);
        const sfsDuels = sfsBattle.getSFSArray('duels');
        const duels: Duel[] = [];

        for (let j = 0; j < sfsDuels.size(); j++) {
            const d = sfsDuels.getSFSObject(j);
            duels.push({
                r: d.getInt('r'),
                s: d.getInt('s'),
                g: d.getInt('g'),
            });
        }

        const extra = sfsBattle.containsKey('extra_pts')
                      ? sfsBattle.getSFSObject('extra_pts')
                      : null;

        battles.push({
            wins: sfsBattle.getInt('wins'),
            total_pts: sfsBattle.getInt('total_pts'),
            duels,
            status: sfsBattle.getUtfString('status') as Battle['status'],
            extra_pts: extra
                       ? {
                    sweep: extra.getInt('sweep'),
                    btl: extra.getInt('btl'),
                }
                       : { sweep: 0, btl: 0 },
        });
    }

    // Fill in empty battles if fewer than 4
    while (battles.length < 4) {
        battles.push({
            wins: 0,
            total_pts: 0,
            duels: [],
            status: 'progress',
            extra_pts: { sweep: 0, btl: 0 },
        });
    }

    return {
        battles,
        currentBattleIndex: battles.findIndex((b) => b.status === 'progress') !== -1
                            ? battles.findIndex((b) => b.status === 'progress')
                            : battles.length - 1,
    };
}

// war-slice.ts
// war-slice.ts
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface Duel {
    r: number; // tricks taken
    s: number; // score (positive: win, zero or less: loss)
    g: number; // goal: tricks needed to win
}

export interface ExtraPoints {
    sweep: number;
    btl: number;
}

export interface Battle {
    duels: Duel[];
    wins: number;
    total_pts: number;
    extra_pts?: ExtraPoints;
    status: 'loss' | 'win' | 'progress';
}

export interface WarState {
    battles: Battle[];
    currentBattleIndex: number;
}

const initialState: WarState = {
    battles: [],
    currentBattleIndex: 0,
};

export const warSlice = createSlice({
    name: 'war',
    initialState,
    reducers: {
        setWarData: (state, action: PayloadAction<WarState>) => {
            return action.payload;
        },
        updateBattle: (
            state,
            action: PayloadAction<{ index: number; battle: Partial<Battle> }>
        ) => {
            const { index, battle } = action.payload;
            if (state.battles[index]) {
                state.battles[index] = { ...state.battles[index], ...battle };
            }
        },
        setCurrentBattleIndex: (state, action: PayloadAction<number>) => {
            state.currentBattleIndex = action.payload;
        },
    },
});

export const selectOverallWarStatus = (state: WarState): Battle['status'] => {
    if (state.battles.some((b) => b.status === 'progress')) {
        return 'progress';
    }
    if (state.battles.every((b) => b.status === 'win')) {
        return 'win';
    }
    return 'loss';
};

export const selectCurrentBattle = (state: WarState): Battle | undefined => {
    return state.battles[state.currentBattleIndex];
};
export const {
    setWarData,
    updateBattle,
    setCurrentBattleIndex,
} = warSlice.actions;

export const warActions = warSlice.actions;
export default warSlice.reducer;